// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import './PropertyField.scss';
import { useTranslation } from 'react-i18next';

import { doc } from 'firebase/firestore';
import { firestore } from '../../context/firebase';
import { format } from 'date-fns';
import { debounce } from 'lodash';

import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import { DELIVERY_SETTINGS_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import CommonModal from '../../shared_components/CommonModal/CommonModal';
import { useGetDocument } from '../../shared_components/hooks/useGetDocument';
import { useSetDocument } from '../../shared_components/hooks/useSetDocument';

export default function PropertyField(props) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const [value, setValue] = React.useState(props.value);
  const [showAddOptionModal, setShowAddOptionModal] = React.useState(false);
  const inputRef = React.useRef(null);
  const newOptionRef = React.useRef(null);

  // Update value when props change
  React.useEffect(() => {
    setValue(props.value ?? '');
  }, [props.value]);

  function datetimeToString(datetime, seconds = false) {
    /** Convert a datetime object to string */
    if (seconds) {
      return format(datetime, "yyyy-MM-dd'T'HH:mm:ss");
    } else {
      return format(datetime, "yyyy-MM-dd'T'HH:mm");
    }
  }

  const deliveriesSettingsRef = doc(firestore, DELIVERY_SETTINGS_DOCUMENT_PATH(currentUserClientId));
  const { data: deliveriesData } = useGetDocument(deliveriesSettingsRef, {
    withSnapshot: true,
    initialCall: props.type === 'select-extendible',
  });
  const { setDocument: setDeliveriesSettings } = useSetDocument(deliveriesSettingsRef);

  // Derive options based on field type
  const options = useMemo(() => {
    // For select and select-extendible-custom, use props.options
    if (props.type === 'select' || props.type === 'select-extendible-custom') {
      return props.options || {};
    }
    // For select-extendible, derive from deliveriesData
    if (props.type === 'select-extendible') {
      const newOptions = deliveriesData?.[props.name] ?? {};
      // Check if the current value is not in the options list. If not, add it.
      if (value && !Object.keys(newOptions).includes(value)) {
        newOptions[value] = value;
      }
      return newOptions;
    }

    return {};
  }, [props.type, props.options, props.name, deliveriesData, value]);

  function handleClick() {
    /* Focus on the input element when clicking on any place on the field */
    if (props.editable && inputRef.current && !showAddOptionModal) {
      inputRef.current.focus();
    }
    if (props.onClick) {
      props.onClick();
    }
  }

  function handleChangesWithDebouncing(event) {
    /* 
        Handle changes in input fields with debouncing.
        Update state immediately, but only trigger the update function (for firebase) after 1 second.
        */
    setValue(event.target.value);
    uploadChanges(event);
  }

  const uploadChanges = React.useCallback(
    debounce((event) => {
      // Update value with a debouncing timer.
      props.handleChange(event);
    }, 700),
    [props.handleChange],
  );

  function renderView() {
    return <p className='property--value'>{value}</p>;
  }

  function renderInput() {
    const input = (
      <input
        className='property--value'
        type={props.type ? props.type : 'text'}
        onChange={handleChangesWithDebouncing}
        name={props.name}
        value={value}
        ref={inputRef}
      />
    );

    // Wrap number input with unit display if unit is provided
    if (props.type === 'number' && props.unit) {
      return (
        <div className='property--value-with-unit'>
          {input}
          <span className='property--unit'>{props.unit}</span>
        </div>
      );
    }

    return input;
  }

  function renderDatetimeLocal() {
    return (
      <input
        className='property--value'
        type='datetime-local'
        onChange={(e) => {
          handleChangesWithDebouncing({ ...e, target: { name: props.name, value: new Date(e.target.value) } });
        }}
        name={props.name}
        value={value ? datetimeToString(value) : ''}
        ref={inputRef}
      />
    );
  }

  function renderTextArea() {
    return (
      <textarea
        className='property--value property--value-textarea'
        onChange={handleChangesWithDebouncing}
        name={props.name}
        value={value}
        ref={inputRef}
      />
    );
  }

  function renderSelect() {
    if (Object.keys(props.options).length > 1) {
      return (
        <select
          className='property--value'
          onChange={props.handleChange}
          name={props.name}
          value={value}
          ref={inputRef}
        >
          <option value='' disabled></option>
          {Object.entries(options)
            .sort(([, valueA], [, valueB]) => {
              const safeValueA = valueA?.toString() || '';
              const safeValueB = valueB?.toString() || '';
              return safeValueA.localeCompare(safeValueB, undefined, { numeric: true });
            })
            .map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
        </select>
      );
    } else {
      return <p className='property--value'>{props.options[value]}</p>;
    }
  }

  function renderMultipleChoiceSelect() {
    return (
      <MultiSelectDropdown
        selectedOptions={value}
        options={props.options}
        onChange={(e) => {
          props.handleChange({ target: { name: props.name, value: e } });
        }}
      />
    );
  }

  function renderSelectExtendible() {
    /* 
        Render a select field with an additional button to add new options.
        Options of default select-extendible fields are stored in the user document in the Firestore database.
        */
    function handleAddOptionButtonClicked() {
      /* 
            Handle when the + button is clicked.
            For select-extendible-custom fields, let the parent component handle the addition of a new option.
            For default select-extendible fields, show a modal to add a new option.
            */
      if (props.type == 'select-extendible-custom') {
        if (props.onAddOption) {
          props.onAddOption();
        }
      } else {
        setShowAddOptionModal(true);
      }
    }

    function addOption() {
      /* Add a new option to the select field. Check that it is valid first. Update on firestore */
      if (!newOptionRef.current.value || newOptionRef.current.value.length === 0) {
        setShowAddOptionModal(false);
        return;
      }
      const newOptionKey = newOptionRef.current.value.toLowerCase().replace(/ /g, '_');
      setDeliveriesSettings(
        {
          [props.name]: {
            [newOptionKey]: newOptionRef.current.value,
          },
        },
        { merge: true },
      );
      handleChangesWithDebouncing({ target: { name: props.name, value: newOptionKey } });
      setShowAddOptionModal(false);
    }

    return (
      <div className='property--select-extendible'>
        <select
          className='property--value'
          onChange={handleChangesWithDebouncing}
          name={props.name}
          value={value}
          ref={inputRef}
        >
          <option key={0} value={null}></option>
          {Object.entries(options)
            .sort(([, valueA], [, valueB]) => {
              const safeValueA = valueA?.toString() || '';
              const safeValueB = valueB?.toString() || '';
              return safeValueA.localeCompare(safeValueB, undefined, { numeric: true });
            })
            .map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
        </select>
        <button className='property--extend-button' onClick={handleAddOptionButtonClicked}>
          +
        </button>

        <CommonModal
          show={showAddOptionModal}
          title={t('property_field_extendable_new')}
          confirmText={t('general_add')}
          cancelText={t('general_cancel')}
          onConfirm={addOption}
          onCancel={() => setShowAddOptionModal(false)}
          theme='dark'
        >
          <input
            type='text'
            ref={newOptionRef}
            className='box-border w-full outline-none text-white p-1.5 rounded-md bg-blue-400/20 border border-blue-400 focus:ring-1 focus:ring-blue-500 transition'
          />
        </CommonModal>
      </div>
    );
  }

  function renderValueField() {
    switch (props.type) {
      case 'textarea':
        return renderTextArea();
      case 'text':
        return renderInput();
      case 'number':
        return renderInput();
      case 'date':
        return renderInput();
      case 'datetime-local':
        return renderDatetimeLocal();
      case 'select':
        return renderSelect();
      case 'multiple-choice-select':
        return renderMultipleChoiceSelect();
      case 'select-extendible':
        return renderSelectExtendible();
      case 'select-extendible-custom':
        return renderSelectExtendible();
      default:
        return renderView();
    }
  }

  function renderUneditableField() {
    let displayValue = value;
    if (props.type === 'select' && props.options) {
      displayValue = props.options[value];
    }

    return <p className='property--value'>{displayValue}</p>;
  }

  return (
    <div
      className={props.highlight ? 'property-field property--highlight' : 'property-field'}
      style={{
        gridColumn: props.grid_column ? props.grid_column : 'span 1',
        gridRow: props.grid_row ? props.grid_row : 'span 1',
      }}
      onClick={handleClick}
    >
      {typeof props.icon === 'string' ? (
        <img className='property--icon' src={props.icon} />
      ) : (
        <div className='property--icon-react'>{props.icon}</div>
      )}
      <p className='property--title'>{props.title}</p>
      {props.editable ? renderValueField() : renderUneditableField()}
    </div>
  );
}
